/**
 * =============================================================================
 * QUALITY BADGE COMPONENT
 * =============================================================================
 *
 * Displays a quality score badge for questions (P1-28).
 *
 * EXTRACTED FROM: QuestionQueue.tsx (P1-17)
 *
 * =============================================================================
 */

import React from 'react';
import { Box } from '@bigcommerce/big-design';
import type { QualityScore } from '@find-my-part/types';
import { tokens } from '../../tokens';

/**
 * Quality badge colors for different quality levels.
 */
const QUALITY_COLORS: Record<QualityScore['level'], { bg: string; text: string; label: string }> = {
  LOW: { ...tokens.color.quality.low, label: 'Low' },
  MEDIUM: { ...tokens.color.quality.medium, label: 'Med' },
  HIGH: { ...tokens.color.quality.high, label: 'High' },
};

/**
 * Props for QualityBadge component.
 */
export interface QualityBadgeProps {
  /** Quality score to display */
  qualityScore: QualityScore;
}

/**
 * Quality badge component for displaying quality score.
 *
 * Displays a colored badge with the quality level and score.
 * Includes a tooltip with detailed factor breakdown on hover.
 *
 * @example
 * ```tsx
 * <QualityBadge qualityScore={{ score: 25, level: 'MEDIUM', factors: { ... } }} />
 * ```
 */
export function QualityBadge({ qualityScore }: QualityBadgeProps): React.ReactElement {
  const colors = QUALITY_COLORS[qualityScore.level];

  return (
    <Box
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: tokens.dimension.space.xs,
        padding: `${tokens.dimension.space.xxs} ${tokens.dimension.space.sm}`,
        borderRadius: tokens.dimension.radius.sm,
        backgroundColor: colors.bg,
        fontSize: tokens.typography.scale.caption.fontSize,
        fontWeight: 500,
      }}
      title={`Quality: ${qualityScore.score}/40\nTitle: ${qualityScore.factors.titleLength}/10\nDescription: ${qualityScore.factors.descriptionLength}/15\nImages: ${qualityScore.factors.hasImages}/10\nGrammar: ${qualityScore.factors.grammar}/5`}
    >
      <span style={{ color: colors.text }}>{colors.label}</span>
      <span style={{ color: colors.text, opacity: 0.7 }}>{qualityScore.score}</span>
    </Box>
  );
}
